import React from 'react';
import { useLocation } from 'react-router-dom';
import ErrorState from './ErrorState';
import analytics from '../services/analytics';

interface BoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryProps extends BoundaryProps {
  /** When this changes while the error panel shows, try the page again. */
  resetKey?: string;
}

interface BoundaryState {
  hasError: boolean;
}

/**
 * Catches a render error in one page and shows a short message with a Reload
 * button. The rest of the app keeps working.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, BoundaryState> {
  state: BoundaryState = { hasError: false };

  static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Page render error:', error, info?.componentStack);
    try {
      analytics.track('page_render_error', {
        path: typeof window !== 'undefined' ? window.location.pathname : null,
        error_name: error?.name || null,
        error_message: String(error?.message || '').slice(0, 300),
      });
    } catch {}
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorState
          message="Something went wrong on this page."
          onRetry={this.handleReload}
          retryLabel="Reload"
          style={{ minHeight: '60vh' }}
        />
      );
    }
    return this.props.children;
  }
}

/** Clears the error panel when the route changes, so the next page can render. */
const AppErrorBoundary: React.FC<BoundaryProps> = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
};

export default AppErrorBoundary;
